import React, { useEffect, useState } from "react";
import "./ChooseProduct.scss";
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  TableColumnsType,
  TableProps,
  Image,
  Table,
} from "antd";
import { DownOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import Helpers from "@utils/Helpers";
import { Link, useNavigate } from "react-router-dom";
import { errorLog } from "logging";
import { ProductApi } from "services/ProductApi";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "contexts/UserContext";
import { useUnit } from "contexts/UnitContext";

type TablePagination<T extends object> = NonNullable<
  Exclude<TableProps<T>["pagination"], boolean>
>;
type TablePaginationPosition<T extends object> = NonNullable<
  TablePagination<T>["position"]
>[number];

interface StepProps {
  adData: any;
  setAdData: (adData: any) => void;
  setSelectedProduct: (product: any) => void;
}

const ChooseProduct: React.FC<StepProps> = ({
  adData,
  setAdData,
  setSelectedProduct,
}) => {
  const [bottom] =
    useState<TablePaginationPosition<SelectType>>("bottomCenter");
  const [isSetAd, setIsSetAd] = useState(false);
  const [category, setCategory] = useState("Main product");
  const [myProducts, setMyProducts] = useState<any>(null);
  const { isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const { units, convertCurrency } = useUnit();
  const navigate = useNavigate();

  interface SelectType {
    key: React.Key;

    name: object;
    sales: string;
    price: string;
    stock: string;
  }

  useEffect(() => {
    const fetchMyProduct = async () => {
      try {
        const response = await ProductApi.GetMyProducts();
        if (response.status === 200) {
          const data = response.data.data;
          const filteredData = data.filter((product: any) => {
            const isSetAd = product.ad;
            return !isSetAd || new Date(product.ad.end_time) < new Date();
          });
          setMyProducts(filteredData);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("ProductCatalog.tsx - fetchMyProduct", error);
      }
    };

    if (isAuthenticated && !myProducts && userProfile?.type_ids.includes(2)) {
      fetchMyProduct();
    }
  }, [isAuthenticated, myProducts]);

  const columns: TableColumnsType<SelectType> = [
    {
      title: "",
      width: "max-content",
      dataIndex: "action",
      key: "1",
      fixed: "left",
    },
    {
      title: "NAME",
      width: "max-content",
      dataIndex: "name",
      key: "2",
      fixed: "left",
    },
    {
      title: "MONTHLY SALES",
      width: "max-content",
      dataIndex: "sales",
      key: "3",
    },
    {
      title: "PRICE",
      width: "max-content",
      dataIndex: "price",
      key: "4",
    },
    {
      title: "STOCK",
      width: "max-content",
      dataIndex: "stock",
      key: "5",
    },
  ];

  const mainProduct: SelectType[] = myProducts?.map((item: any) => {
    return {
      key: item.product_id,

      name: (
        <div className="item">
          <Image
            className="image"
            src={item.images[0]}
            preview={false}
            onClick={() => {
              window.open(`/product/detail?productId=${item.product_id}`);
            }}
          />
          <div
            className="name"
            onClick={() => {
              window.open(`/product/detail?productId=${item.product_id}`);
            }}
          >
            {item.hay_type} <span className="id">ID: {item.product_id}</span>
          </div>
        </div>
      ),
      sales: "800 Bales / mon",
      price: `$${Helpers.formatNumber(
        convertCurrency(
          item.unit == "Ton" ? item.price_per_tons : item.price_per_bale,
          "USD",
          units.currency
        ),
        2
      )} / ${item.unit}`,
      stock: `${item.quantity} ${item.unit}`,
    };
  });

  const rentalMachine: SelectType[] = [
    {
      key: "1",

      name: (
        <div className="item">
          <Image
            className="image"
            src={Helpers.getImageFrontPage("hay")}
            preview={false}
          />
          <div className="name">
            Grass cutter <span className="id">ID: 123456789</span>
          </div>
        </div>
      ),
      sales: "50 items/ mon",
      price: "$50 / day",
      stock: "10 items",
    },
  ];

  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);
  const rowSelection: TableProps<SelectType>["rowSelection"] = {
    type: "radio",
    selectedRowKeys: selectedRowKey !== null ? [selectedRowKey] : [],
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKey(selectedRowKeys[0]);
      const selectedProduct = myProducts.find(
        (item: any) => item.product_id === selectedRowKeys[0]
      );
      setSelectedProduct(selectedProduct);
      setAdData({
        ...adData,
        product_id: [0],
      });
      setIsSetAd(selectedRowKeys.length > 0);
    },
  };

  return (
    <div className="ChooseProduct">
      <div className="item-title">
        <b>My current product listing</b>
        <div className="available-slot">
          Available ad slots{" "}
          <Button
            icon={<PlusOutlined />}
            iconPosition="end"
            onClick={() => navigate("/profile?direct=add-slot")}
          >
            0
          </Button>
        </div>
      </div>
      <div className="search-group">
        <Input
          className="search-input"
          size="large"
          variant="filled"
          prefix={<SearchOutlined />}
          placeholder="Search Product Name / ID"
        />
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: (
                  <div className="item">
                    <Checkbox checked={category === "All category"} /> All
                    category
                  </div>
                ),
                onClick: () => {
                  setCategory("All category");
                },
              },
              {
                key: "2",
                label: (
                  <div className="item">
                    <Checkbox checked={category === "Main product"} /> Main
                    product
                  </div>
                ),
                onClick: () => {
                  setCategory("Main product");
                },
              },
              {
                key: "3",
                label: (
                  <div className="item">
                    <Checkbox checked={category === "Rental machine"} /> Rental
                    machine
                  </div>
                ),
                onClick: () => {
                  setCategory("Rental machine");
                },
              },
              {
                key: "4",
                label: (
                  <div className="item">
                    <Checkbox checked={category === "Service"} /> Service
                  </div>
                ),
                onClick: () => {
                  setCategory("Service");
                },
              },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            size="large"
            className="filter-btn"
            iconPosition="end"
            icon={<DownOutlined />}
          >
            {category}
          </Button>
        </Dropdown>
      </div>
      {(category === "Main product" || category === "All category") && (
        <div className="select-content">
          <div className="product-list">
            <div className="item-title">
              <b>Result</b>
              <span className="description">
                <Checkbox /> Show available products only
              </span>
            </div>
            <Table<SelectType>
              className="custom-table"
              pagination={{ position: [bottom] }}
              rowSelection={rowSelection}
              columns={columns}
              dataSource={mainProduct}
              scroll={{ x: "max-content" }}
              onRow={(record) => ({
                onClick: () => {
                  rowSelection.onChange?.([record.key], [], { type: "single" });
                },
              })}
            />
          </div>
        </div>
      )}
      {(category === "Rental machine" || category === "Service") && (
        <div className="select-content">
          <Link to={""} className="service-create-btn">
            <PlusOutlined />
            Create a service
          </Link>
          <div className="product-list">
            <div className="item-title">
              <b>Result</b>
            </div>
            <Table<SelectType>
              className="custom-table"
              pagination={{ position: [bottom] }}
              rowSelection={{ ...rowSelection }}
              columns={columns}
              dataSource={rentalMachine}
              scroll={{ x: "max-content" }}
              onRow={(record) => ({
                onClick: () => {
                  rowSelection.onChange?.([record.key], [], { type: "single" });
                },
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChooseProduct;
